import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { uniqBy } from 'lodash';
import { useQuery } from '@apollo/client';
import { makeStyles } from '@material-ui/core';
import MastercodeUnlockAnnouncement from 'components/@common/Announcements/MastercodeUnlockAnnouncement';
import PageContainer from 'components/@layout/PageContainer';
import GET_LICENSE_USAGE from 'graph/orders/get-license-usage-query';
import useUiStore from 'store/ui';
import { ModuleProps } from 'types';
import { Mastercode, Organization } from 'types/schema-generated-types';
import { NumberParam, withDefault, useQueryParams } from 'use-query-params';
import HeaderBar from '../HeaderBar';
import ProductFamilyTileView from './components/ProductFamilyUsageTileView';
import ProductLicenseUsageFilterBar from './components/ProductLicenseUsageFilterBar';
import LicenseDetailsModal from './modals/LicenseDetailsModal';

const getRootOrganizationLicenseUsage = (organization?: Organization) => {
  if (!organization?.parentOrganizationId || !organization?.rootOrganization?.licenseUsage) return;
  return organization.rootOrganization.licenseUsage;
};

const AdminProductLicenses: React.FC<ModuleProps> = props => {
  const classes = useStyles();
  const [, { openFullModal, closeModal }] = useUiStore();
  const { t } = useTranslation('AdminProductLicenses');

  const [{ organizationId }, setQuery] = useQueryParams({
    organizationId: withDefault(NumberParam, null)
  });

  const { data, error, loading } = useQuery(GET_LICENSE_USAGE, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first'
  });

  const { personalMastercodes, organizations } = useMemo(() => {
    const personalMastercodes: Mastercode[] = data?.mastercodes ?? [];
    const organizations: Organization[] = data?.organizations ?? [];
    return { personalMastercodes, organizations };
  }, [data]);

  useEffect(() => {
    if (
      organizations.length &&
      (!organizationId || !organizations.some(o => o.id === organizationId))
    ) {
      const rootOrgs = organizations.filter(o => !o.parentOrganizationId);
      // Default to a root organization if possible.
      if (rootOrgs.length) setQuery({ organizationId: rootOrgs[0].id });
      // Otherwise, just use the first in the list.
      else setQuery({ organizationId: organizations[0].id });
    }
  }, [organizationId, organizations, setQuery]);

  const unlockedMastercodes = useMemo(() => {
    const allMastercodes = organizations.flatMap(organization => [
      ...(organization.mastercodes ?? []),
      ...(organization.ancestorMastercodes ?? [])
    ]);

    return uniqBy(allMastercodes, 'id');
  }, [organizations]);

  const handleOrganizationChange = (organizationId: number) => setQuery({ organizationId });
  const handleLicenseDetailsClick = (productCode: string): void => {
    openFullModal({
      title: t('licenseDetailsTitle'),
      body: <LicenseDetailsModal mastercodes={unlockedMastercodes} onClose={closeModal} />,
      routeAction: 'license-details',
      routeParams: { product: productCode }
    });
  };

  const organization = organizations.find(o => o.id === organizationId);
  const licenseUsage = organization?.licenseUsage ?? [];
  const rootOrganizationLicenseUsage = getRootOrganizationLicenseUsage(organization);

  return (
    <PageContainer loading={loading} error={error} {...props}>
      <HeaderBar {...props} />
      {organizations.length > 1 && (
        <ProductLicenseUsageFilterBar
          organizations={organizations}
          selectedOrganizationId={organizationId}
          onChange={handleOrganizationChange}
        />
      )}
      <div className={classes.container}>
        <div className={classes.announcementsContainer}>
          <MastercodeUnlockAnnouncement
            href="/admin/products-licenses/orders"
            mastercodes={personalMastercodes}
          />
        </div>
        {licenseUsage.length > 0 && (
          <div className={classes.productTileContainer}>
            <ProductFamilyTileView
              onLicenseDetailsClick={handleLicenseDetailsClick}
              licenseUsage={licenseUsage}
              rootOrganizationLicenseUsage={rootOrganizationLicenseUsage}
            />
          </div>
        )}
      </div>
    </PageContainer>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    padding: `${theme.spacing(3)}px ${theme.spacing(5)}px`
  },
  announcementsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${theme.spacing(4)}px`
  },
  header: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  productTileContainer: {
    paddingBottom: theme.spacing(2)
  }
}));

export default AdminProductLicenses;
